import { useEffect, useState } from 'react';
import {
  BarChart3,
  TrendingUp,
  Package,
  Truck,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { PageHeader, MetricCard, KPIGrid } from '@/components/common';
import { analyticsApi, DashboardData, Recommendation } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
} from 'recharts';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';

// Chart colors - using CSS custom properties from design system
const STOCK_COLORS = {
  optimal: 'var(--color-success-500)',
  low: 'var(--color-warning-500)',
  stockout: 'var(--color-danger-500)',
  overstock: 'var(--color-primary-500)',
};

const PO_COLORS = [
  'var(--color-primary-500)',
  'var(--color-accent)',
  'var(--color-success-500)',
  'var(--color-warning-500)',
  'var(--color-danger-500)',
  'var(--color-primary-700)',
  'var(--color-accent-foreground)',
];

const priorityColors: Record<string, string> = {
  CRITICAL: 'bg-danger-50 text-danger-500',
  HIGH: 'bg-warning-50 text-warning-600',
  MEDIUM: 'bg-warning-50 text-warning-600',
  LOW: 'bg-primary-50 text-primary-500',
};

export default function Analytics() {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [dashboardData, recommendationsData] = await Promise.all([
        analyticsApi.getDashboard(),
        analyticsApi.getRecommendations(),
      ]);
      setDashboard(dashboardData);
      setRecommendations(recommendationsData);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleActionRecommendation = async (id: string) => {
    try {
      await analyticsApi.markRecommendationActioned(id);
      toast.success('Recommendation marked as actioned');
      setRecommendations(recommendations.filter((r) => r.id !== id));
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Utility function for currency formatting (currently unused)
  // const formatCurrency = (amount?: number) => {
  //   if (!amount) return '$0';
  //   return new Intl.NumberFormat('en-US', {
  //     style: 'currency',
  //     currency: 'USD',
  //     maximumFractionDigits: 0,
  //   }).format(amount);
  // };

  if (loading) {
    return (
      <div className="space-y-5">
        <PageHeader
          title="Analytics"
          description="Operational trends across inventory, equipment, and freight, with the recommendations derived from them."
        />
        <div className="flex items-center justify-center h-64">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Analytics"
          description="Insights and recommendations for your supply chain"
        />
      </motion.div>

      {/* Overview KPIs */}
      {dashboard && (
        <div className="mt-6">
          <KPIGrid columns={4}>
            <MetricCard
              label="Total Items"
              value={dashboard.metrics.totalItems}
              sentiment="info"
              icon={<Package className="h-5 w-5" />}
            />
            <MetricCard
              label="Inventory Value"
              value={dashboard.metrics.totalInventoryValue}
              sentiment="success"
              prefix="$"
              icon={<BarChart3 className="h-5 w-5" />}
            />
            <MetricCard
              label="Active Shipments"
              value={dashboard.metrics.totalShipments}
              sentiment="neutral"
              icon={<Truck className="h-5 w-5" />}
            />
            <MetricCard
              label="Active Alerts"
              value={dashboard.metrics.activeAlerts}
              sentiment={dashboard.metrics.activeAlerts > 0 ? 'warning' : 'success'}
              icon={<AlertTriangle className="h-5 w-5" />}
              trend={
                dashboard.metrics.activeAlerts > 0
                  ? { direction: 'up', value: '', text: 'needs attention' }
                  : undefined
              }
            />
          </KPIGrid>
        </div>
      )}

      <motion.div variants={fadeUp} className="grid grid-cols-1 gap-6 lg:grid-cols-2 mt-6">
        {/* Stock Distribution - Pie Chart */}
        {dashboard && (
          <Card className="border-border hover:shadow-raised-hover transition-all duration-200">
            <CardHeader>
              <h3 className="text-base font-semibold text-foreground tracking-tight">
                <Package className="h-5 w-5" />
                Stock Distribution
              </h3>
              <CardDescription className="text-muted-foreground">
                Inventory status breakdown
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const stockData = [
                  {
                    name: 'Optimal',
                    value: dashboard.distributions.inventory.optimal,
                    color: STOCK_COLORS.optimal,
                  },
                  {
                    name: 'Low Stock',
                    value: dashboard.distributions.inventory.low,
                    color: STOCK_COLORS.low,
                  },
                  {
                    name: 'Out of Stock',
                    value: dashboard.distributions.inventory.stockout,
                    color: STOCK_COLORS.stockout,
                  },
                  {
                    name: 'Overstock',
                    value: dashboard.distributions.inventory.overstock,
                    color: STOCK_COLORS.overstock,
                  },
                ].filter((item) => item.value > 0);

                const total = stockData.reduce((sum, item) => sum + item.value, 0);

                if (total === 0) {
                  return (
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      No inventory data available
                    </div>
                  );
                }

                return (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={stockData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={false}
                        >
                          {stockData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value: number) => [value, 'Items']}
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}
              {/* Legend */}
              <div className="mt-4 grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: STOCK_COLORS.optimal }}
                  />
                  <span className="text-sm">
                    Optimal ({dashboard.distributions.inventory.optimal})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: STOCK_COLORS.low }}
                  />
                  <span className="text-sm">Low ({dashboard.distributions.inventory.low})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: STOCK_COLORS.stockout }}
                  />
                  <span className="text-sm">
                    Out ({dashboard.distributions.inventory.stockout})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div
                    className="h-3 w-3 rounded-full"
                    style={{ backgroundColor: STOCK_COLORS.overstock }}
                  />
                  <span className="text-sm">
                    Over ({dashboard.distributions.inventory.overstock})
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* PO Status Distribution - Bar Chart */}
        {dashboard && (
          <Card className="border-border hover:shadow-raised-hover transition-all duration-200">
            <CardHeader>
              <h3 className="text-base font-semibold text-foreground tracking-tight">
                <TrendingUp className="h-5 w-5" />
                Purchase Order Status
              </h3>
              <CardDescription className="text-muted-foreground">
                Current PO pipeline
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                const poData = Object.entries(dashboard.distributions.purchaseOrders)
                  .map(([status, count]) => ({
                    name: status
                      .replace(/_/g, ' ')
                      .toLowerCase()
                      .replace(/\b\w/g, (l) => l.toUpperCase()),
                    count: count as number,
                  }))
                  .filter((item) => item.count > 0);

                if (poData.length === 0) {
                  return (
                    <div className="flex items-center justify-center h-64 text-muted-foreground">
                      No purchase order data available
                    </div>
                  );
                }

                return (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={poData} layout="vertical" margin={{ left: 20, right: 20 }}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          horizontal={true}
                          vertical={false}
                          stroke="hsl(var(--border))"
                        />
                        <XAxis
                          type="number"
                          allowDecimals={false}
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={100}
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tickLine={false}
                        />
                        <Tooltip
                          formatter={(value: number) => [value, 'Orders']}
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                        <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                          {poData.map((_, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={PO_COLORS[index % PO_COLORS.length]}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}
      </motion.div>

      {/* Trend Chart - Inventory Value Over Time (Mock Data) */}
      {dashboard && (
        <motion.div variants={fadeUp} className="mt-6">
          <Card className="border-border hover:shadow-raised-hover transition-all duration-200">
            <CardHeader>
              <h3 className="text-base font-semibold text-foreground tracking-tight">
                <BarChart3 className="h-5 w-5" />
                Inventory Value Trend
              </h3>
              <CardDescription className="text-muted-foreground">
                Last 7 days inventory value movement
              </CardDescription>
            </CardHeader>
            <CardContent>
              {(() => {
                // Generate mock trend data based on current value
                const currentValue = dashboard.metrics.totalInventoryValue || 100000;
                const trendData = Array.from({ length: 7 }, (_, i) => {
                  const daysAgo = 6 - i;
                  const date = new Date();
                  date.setDate(date.getDate() - daysAgo);
                  // Add some variance to make it look realistic
                  const variance = (Math.random() - 0.5) * 0.1;
                  const value = currentValue * (0.95 + i * 0.01 + variance);
                  return {
                    date: date.toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                    }),
                    value: Math.round(value),
                  };
                });

                return (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart
                        data={trendData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis
                          dataKey="date"
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tickLine={false}
                        />
                        <YAxis
                          stroke="hsl(var(--muted-foreground))"
                          fontSize={12}
                          tickLine={false}
                          tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                        />
                        <Tooltip
                          formatter={(value: number) => [`$${value.toLocaleString()}`, 'Value']}
                          contentStyle={{
                            backgroundColor: 'hsl(var(--card))',
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px',
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="value"
                          stroke="var(--color-primary-500)"
                          strokeWidth={2}
                          dot={{ fill: 'var(--color-primary-500)', strokeWidth: 2 }}
                          activeDot={{ r: 6, fill: 'var(--color-primary-500)' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* AI Recommendations */}
      <motion.div variants={fadeUp} className="mt-6">
        <Card className="border-border hover:shadow-raised-hover transition-all duration-200">
          <CardHeader>
            <h3 className="text-base font-semibold text-foreground tracking-tight">
              <Lightbulb className="h-5 w-5 text-warning-600" />
              AI Recommendations
            </h3>
            <CardDescription className="text-muted-foreground">
              Smart suggestions to optimize your supply chain
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recommendations.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <CheckCircle className="h-12 w-12 text-success-600 mb-4" />
                <p className="text-lg font-medium text-foreground">All caught up!</p>
                <p className="text-muted-foreground">No recommendations at this time.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {recommendations.map((rec) => (
                  <div
                    key={rec.id}
                    className="flex items-start justify-between gap-4 rounded-lg border border-border p-4 hover:shadow-raised-hover transition-all duration-150"
                  >
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <Badge className={priorityColors[rec.priority]}>{rec.priority}</Badge>
                        <span className="text-sm text-muted-foreground">{rec.type}</span>
                      </div>
                      <h4 className="text-sm font-semibold">{rec.title}</h4>
                      <p className="text-sm text-muted-foreground">{rec.description}</p>
                      {rec.suggestedAction && (
                        <p className="text-sm text-primary">
                          Suggested: {rec.suggestedAction.action}
                          {rec.suggestedAction.quantity &&
                            ` (Qty: ${rec.suggestedAction.quantity})`}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleActionRecommendation(rec.id)}
                    >
                      Mark Done
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Recent Activity */}
      {dashboard && (
        <motion.div variants={fadeUp} className="grid grid-cols-1 gap-6 lg:grid-cols-3 mt-6">
          {/* Recent POs */}
          <Card className="border-border hover:shadow-raised-hover transition-all duration-200">
            <CardHeader>
              <h3 className="text-base font-semibold text-foreground tracking-tight">
                Recent Purchase Orders
              </h3>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard.recentActivity.purchaseOrders.slice(0, 5).map((po) => (
                  <div key={po.id} className="flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium">{po.poNumber}</div>
                      <div className="text-muted-foreground">{po.supplier?.name}</div>
                    </div>
                    <Badge variant="outline">{po.status}</Badge>
                  </div>
                ))}
                {dashboard.recentActivity.purchaseOrders.length === 0 && (
                  <p className="text-sm text-muted-foreground">No recent purchase orders</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Shipments */}
          <Card className="border-border hover:shadow-raised-hover transition-all duration-200">
            <CardHeader>
              <h3 className="text-base font-semibold text-foreground tracking-tight">
                Recent Shipments
              </h3>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard.recentActivity.shipments.slice(0, 5).map((shipment) => (
                  <div key={shipment.id} className="flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium text-foreground">{shipment.shipmentNumber}</div>
                      <div className="text-muted-foreground">
                        {shipment.origin?.name} → {shipment.destination?.name}
                      </div>
                    </div>
                    <Badge variant="outline">{shipment.status}</Badge>
                  </div>
                ))}
                {dashboard.recentActivity.shipments.length === 0 && (
                  <p className="text-sm text-muted-foreground">No recent shipments</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Recent Alerts */}
          <Card className="border-border hover:shadow-raised-hover transition-all duration-200">
            <CardHeader>
              <h3 className="text-base font-semibold text-foreground tracking-tight">
                Recent Alerts
              </h3>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {dashboard.recentActivity.alerts.slice(0, 5).map((alert) => (
                  <div key={alert.id} className="flex items-center justify-between text-sm">
                    <div>
                      <div className="font-medium text-foreground">{alert.title}</div>
                      <div className="text-muted-foreground">{alert.type}</div>
                    </div>
                    <Badge className={priorityColors[alert.severity]}>{alert.severity}</Badge>
                  </div>
                ))}
                {dashboard.recentActivity.alerts.length === 0 && (
                  <p className="text-sm text-muted-foreground">No recent alerts</p>
                )}
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </motion.main>
  );
}
